import { ChipPropsVariantOverrides } from "@mui/material";
import React from "react";
import { DateTime } from "luxon";
import { CompletedCheckInSummary } from "app/containers/TeamsTab/types";
import { getLatestCheckIns } from "app/containers/TeamsTab/helpers";
import { useGetGoalsListQuery } from "app/components/Goal/goalsApi";
import { REFLECTION_STATUS_MAP } from "app/components/Goal/constants";

import type { BoZoneHistorySeries } from "../../layer06";
import { ChatbotConversation } from "app/containers/Chatbot/types";
import { getConversationTitle, useCoachBoConversations } from "../../../common";

export type LearnedItemSource = "bo-chat" | "check-in" | "goal-reflection";

export interface IData {
  id: string;
  source: LearnedItemSource;
  timestamp: number;
  date: string;
  category: string;
  categoryVariant: keyof ChipPropsVariantOverrides;
  summary: string;
  conversation?: ChatbotConversation;
  goalId?: number;
  reflectionId?: number;
}

// Goals are paginated; until there's a dedicated reflections feed endpoint we
// pull a single large page and flatten the reflections off each goal.
const GOALS_PER_PAGE = 50;

const sourceMeta: Record<
  LearnedItemSource,
  { category: string; categoryVariant: keyof ChipPropsVariantOverrides }
> = {
  "bo-chat": { category: "Bo chat", categoryVariant: "info" },
  "check-in": { category: "Check in", categoryVariant: "warning" },
  "goal-reflection": {
    category: "Goal reflection",
    categoryVariant: "success",
  },
};

// Backend timestamps come back as either ISO or SQL ("yyyy-MM-dd HH:mm:ss").
const parseDate = (value?: string | null) =>
  DateTime.fromISO((value ?? "").replace(" ", "T"));

const formatLearnedDate = (date: DateTime) => {
  const today = DateTime.now().startOf("day");
  const day = date.startOf("day");
  if (day.equals(today)) return "Today";
  if (day.equals(today.minus({ days: 1 }))) return "Yesterday";
  return date.toFormat("MMM dd").toUpperCase();
};

const toItem = (
  item: Omit<IData, "date" | "timestamp" | "category" | "categoryVariant">,
  rawDate: string
): IData | null => {
  const date = parseDate(rawDate);
  if (!date.isValid) return null;
  return {
    ...item,
    ...sourceMeta[item.source],
    timestamp: date.toMillis(),
    date: formatLearnedDate(date),
  };
};

const getCheckInSummaryFallBack = (conversation: ChatbotConversation) => {
  const date = parseDate(conversation.created_at);
  const formatted = date.isValid ? date.toFormat("MMM d, yyyy") : "";

  if (!conversation.weeklyCheckInLink) {
    return formatted
      ? `This was a conversation you had on ${formatted}.`
      : "This was a conversation you had with Bo.";
  }

  const isThisWeek =
    date.isValid && date >= DateTime.now().minus({ days: 7 }).startOf("day");
  if (isThisWeek) {
    return "This is this week's check in conversation, please click on the pencil icon to start your check in.";
  }

  return formatted
    ? `This was the check in conversation that was scheduled on ${formatted}.`
    : "This was a scheduled check in conversation.";
};

export function useLearnedItemsQuery(userAccountId: number | null) {
  const { conversations, status: conversationsStatus } =
    useCoachBoConversations();
  const { data: goalsResponse, isLoading: isGoalsLoading } =
    useGetGoalsListQuery(
      {
        viewUserId: userAccountId ?? undefined,
        filter: "all",
        is_org_goal: false,
        perPage: GOALS_PER_PAGE,
      },
      { skip: !userAccountId }
    );

  const data = React.useMemo(() => {
    const items: (IData | null)[] = [];

    // Conversations only show once they've been processed into a summary.
    conversations.forEach((conversation) => {
      const summary =
        getConversationTitle(conversation) ??
        getCheckInSummaryFallBack(conversation);
      const source = conversation.weeklyCheckInLink ? "check-in" : "bo-chat";
      items.push(
        toItem(
          {
            id: `${source}-${conversation.threadId}`,
            source,
            summary,
            conversation,
          },
          conversation.created_at
        )
      );
    });

    goalsResponse?.data?.data?.forEach((goal) => {
      goal.reflections?.forEach((reflection) => {
        const comments = reflection.comments?.trim();
        const status = REFLECTION_STATUS_MAP[reflection.status]?.label;
        const detail =
          comments ||
          [status, `${reflection.progressPercent}% progress`]
            .filter(Boolean)
            .join(", ");
        items.push(
          toItem(
            {
              id: `goal-reflection-${reflection.goalsReflectionsId}`,
              source: "goal-reflection",
              summary: `${goal.title}: ${detail}`,
              goalId: goal.goalId,
              reflectionId: reflection.goalsReflectionsId,
            },
            reflection.created_at
          )
        );
      });
    });

    return items
      .filter((item): item is IData => item !== null)
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [conversations, goalsResponse]);

  const isLoading =
    conversationsStatus === "idle" ||
    conversationsStatus === "loading" ||
    isGoalsLoading;

  return { data, isLoading };
}

export interface IHistory {
  labels: string[];
  pointLabels: string[];
  series: BoZoneHistorySeries[];
}

type HistoryMetric = "readiness" | "progress" | "mood";

const historyConfig: {
  metric: HistoryMetric;
  label: string;
  color: string;
  scoreKey: "readinessScore" | "progressScore" | "moodScore";
}[] = [
  {
    metric: "readiness",
    label: "Readiness",
    color: "#214294",
    scoreKey: "readinessScore",
  },
  {
    metric: "progress",
    label: "Progress",
    color: "#E67800",
    scoreKey: "progressScore",
  },
  { metric: "mood", label: "Mood", color: "#CC0042", scoreKey: "moodScore" },
];

export function useHistoryQuery(
  checkInSummary?: CompletedCheckInSummary | null
) {
  return React.useMemo(() => {
    const checkIns = checkInSummary ? getLatestCheckIns(checkInSummary) : [];

    const series: BoZoneHistorySeries[] = historyConfig.map(
      ({ metric, label, color, scoreKey }) => {
        const trend = checkInSummary?.scoreTrends?.[metric];

        return {
          id: metric,
          label,
          color,
          direction: trend === "up" || trend === "down" ? trend : "flat",
          points: checkIns.map((checkIn) => checkIn[scoreKey] || null),
        };
      }
    );

    const labels: string[] = [];
    const pointLabels: string[] = [];

    checkIns.forEach((checkIn) => {
      // fromISO reads a date-only string as local midnight, not UTC.
      const date = DateTime.fromISO(checkIn.scheduledDate.replace(" ", "T"));
      labels.push(date.toFormat("MMM d"));
      pointLabels.push(date.toFormat("MMM d, yyyy"));
    });

    const data: IHistory = {
      labels,
      pointLabels,
      series,
    };

    return {
      data,
    };
  }, [checkInSummary]);
}
